import { calendarDateIn } from '@mlm/shared/coaching';
import {
  cancelLocalNotifications,
  canScheduleLocalNotifications,
  scheduleDailyReminders,
  type DailyReminder,
  type ReminderScheduleResult,
} from '@mlm/ui/lib/native-shell';

/**
 * The coach app's reminders (SC-42), delivered by the phone itself.
 *
 * A member sets reminder times in their COACHING timezone (the one their day,
 * ritual and score run in), but the phone schedules in its own clock. The two
 * are the same for almost everyone; for a member travelling, or one whose
 * profile zone differs from the phone's, the time is converted so the morning
 * reminder still arrives at the member's morning.
 */

export const MORNING_REMINDER_ID = 1001;
export const EVENING_REMINDER_ID = 1002;
const REMINDER_IDS = [MORNING_REMINDER_ID, EVENING_REMINDER_ID];

/** Blueprint §17 copy. */
export const REMINDER_COPY = {
  morning: { title: 'Miracles Protocol', body: 'Your morning Success Ritual is ready.' },
  evening: { title: 'Miracles Protocol', body: 'Close the day with your 3-minute Success Review.' },
} as const;

export type ReminderPrefs = {
  morningReminder?: string | null;
  eveningReminder?: string | null;
};

function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** The wall-clock parts `instant` shows in `timeZone`. */
function wallClock(instant: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
  };
}

/**
 * `HH:MM` as read in `fromZone` today, expressed on the clock of `toZone`.
 * Null for a malformed time or zone. The zone offset is found by comparing a
 * guess with what the zone's clock shows for it, twice, which settles on the
 * right side of a daylight-saving change.
 */
export function deviceClockFor(
  hhmm: string,
  fromZone: string,
  toZone: string = deviceTimeZone(),
  now: Date = new Date(),
): { hour: number; minute: number } | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!match) return null;
  try {
    const [y, m, d] = calendarDateIn(fromZone, now).split('-').map(Number) as [
      number,
      number,
      number,
    ];
    const target = Date.UTC(y, m - 1, d, Number(match[1]), Number(match[2]));
    let instant = target;
    for (let i = 0; i < 2; i += 1) {
      const shown = wallClock(instant, fromZone);
      const shownUtc = Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute);
      instant += target - shownUtc;
    }
    const local = wallClock(instant, toZone);
    return { hour: local.hour, minute: local.minute };
  } catch {
    return null;
  }
}

/** The reminders these preferences ask for, on the device's clock. */
export function remindersFor(
  prefs: ReminderPrefs,
  timeZone: string,
  deviceZone?: string,
): DailyReminder[] {
  const out: DailyReminder[] = [];
  const add = (
    id: number,
    time: string | null | undefined,
    copy: (typeof REMINDER_COPY)[keyof typeof REMINDER_COPY],
    path: string,
  ) => {
    if (!time) return;
    const clock = deviceClockFor(time, timeZone, deviceZone);
    if (clock) out.push({ id, ...clock, ...copy, extra: { path } });
  };
  add(MORNING_REMINDER_ID, prefs.morningReminder, REMINDER_COPY.morning, '/ritual/morning');
  add(EVENING_REMINDER_ID, prefs.eveningReminder, REMINDER_COPY.evening, '/review/evening');
  return out;
}

/**
 * Make the phone's reminders match the preferences: clear both, then schedule
 * what is switched on. `prompt` allows the permission dialog (only when the
 * member has just pressed Save).
 */
export async function syncReminders(
  prefs: ReminderPrefs,
  timeZone: string,
  opts: { prompt: boolean },
): Promise<ReminderScheduleResult> {
  if (!canScheduleLocalNotifications()) return 'unavailable';
  await cancelLocalNotifications(REMINDER_IDS);
  return scheduleDailyReminders(remindersFor(prefs, timeZone), {
    requestPermission: opts.prompt,
  });
}
